import { Box, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, Typography } from '@mui/material'
import React, { FunctionComponent, useState, useContext, useEffect } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { IMeterReading, IPostMeterReading } from '../../../shared/services/location/location.model'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

export const MeterReadingInputDialog: FunctionComponent<{
  open: boolean;
  onClose: () => void;
  onSubmit: (reading: IPostMeterReading, restForm: () => void, isEdit: boolean) => void;
  meterId: string;
  meterName: string;
  mostRecentReadingDate?: string | null;
  mostRecentReadingAmount?: number | null;
  isSubmitting?: boolean;
  reading?: IMeterReading | null;
  isEdit: boolean;
  errorMessage?: string | null;
  units: string;
}> = ({
  open,
  onClose,
  onSubmit,
  meterId,
  meterName,
  mostRecentReadingDate,
  mostRecentReadingAmount,
  isSubmitting = false,
  reading,
  isEdit = false,
  errorMessage,
  units
}) => {
    //Services
    const reportPeriodContext = useContext(ReportPeriodContext);
    //State
    const [openDate, setOpenDate] = useState(false);
    //Loading State
    const [isLoading, setIsLoading] = useState(false);
    //Variables
    const allowedDates = reportPeriodContext.getAllowedInputDates();
    const selectedReportPeriodId = reportPeriodContext.lastSelectedOpenReportId;

    const validationSchema = Yup.object().shape({
      readingDate: Yup.string()
        .required('Reading date is required')
        .test('not-future', 'Reading date cannot be in the future', function (value) {
          if (!value) return true;
          return moment(value).isSameOrBefore(moment(), 'day');
        })
        .test('more-recent', 'Reading date must be more recent than the last reading', function (value) {
          if (!value || !mostRecentReadingDate) return true;
          return moment(value).isAfter(moment(mostRecentReadingDate), 'day');
        })
        .test('within-report-period', 'Reading date must be within the selected report period', function (value) {
          if (!value || !allowedDates.startDate || !allowedDates.endDate) return true;
          const readingDate = moment(value);
          const startDate = moment(allowedDates.startDate);
          const endDate = moment(allowedDates.endDate);
          return readingDate.isBetween(startDate, endDate, 'day', '[]');
        }),
      meterReadingAmount: Yup.number()
        .required('Reading amount is required')
        .min(0, 'Reading amount must be greater than 0')
        .typeError('Reading amount must be a number')
        .test('greater-or-equal', 'Reading amount must be equal to or greater than the previous reading', function (value) {
          if (value === undefined || value === null || !mostRecentReadingAmount) return true;
          return value >= mostRecentReadingAmount;
        }),
    });

    const formik = useFormik<{
      readingDate: string;
      meterReadingAmount: number | undefined;
    }>({
      initialValues: {
        readingDate: moment().format('YYYY-MM-DD'),
        meterReadingAmount: undefined,
      },
      validationSchema,
      onSubmit: async (values) => {
        const readingData = {
          readingDate: values.readingDate,
          meterReadingAmount: values.meterReadingAmount,
          meterId: meterId,
          reportPeriodId: selectedReportPeriodId,
        };
        onSubmit(readingData, formik.resetForm, isEdit)
      },
    });

    useEffect(() => {
      if (reading) {
        formik.setValues({
          readingDate: reading.readingDate,
          meterReadingAmount: reading.meterReadingAmount,
        });
      }
    }, [reading]);

    const handleClose = () => {
      formik.resetForm();
      onClose();
    };

    return (
      <DialogWrapper
        open={open}
        onClose={handleClose}
        title={isEdit ? 'Edit' : `Add Meter Reading for ${meterName}`}
        cancelHandler={handleClose}
        submitHandler={formik.submitForm}
        disableSaveButton={isLoading}
        isSubmitting={isSubmitting}>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label='Reading Date'
              minDate={moment(reportPeriodContext.getAllowedInputDates()?.startDate)}
              maxDate={moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
              value={formik.values.readingDate ? moment(formik.values.readingDate) : null}
              onChange={(date) => formik.setFieldValue('readingDate', date?.format('YYYY-MM-DD'))}
              format='DD/MM/YYYY'
              sx={{ color: 'text.primary' }}
              open={openDate}
              onOpen={() => setOpenDate(true)}
              onClose={() => setOpenDate(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpenDate(true),
                  error: formik.touched.readingDate && Boolean(formik.errors.readingDate)
                },
              }}
            />
          </LocalizationProvider>
          {formik.touched.readingDate && formik.errors.readingDate &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.readingDate ? <div>{formik.errors.readingDate as string}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='meterReadingAmount'>Reading Amount</InputLabel>
          <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }} id='meterReadingAmount' label='Reading Amount' name='meterReadingAmount' type='number'
            value={formik.values.meterReadingAmount}
            onChange={formik.handleChange}
            endAdornment={<InputAdornment position='end'>{units}</InputAdornment>}
            error={formik.touched.meterReadingAmount && Boolean(formik.errors.meterReadingAmount)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.meterReadingAmount && formik.errors.meterReadingAmount &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.meterReadingAmount ? <div>{formik.errors.meterReadingAmount as string}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        {errorMessage && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography variant="body2">{errorMessage}</Typography>
          </Box>
        )}
      </DialogWrapper>
    );
  };
